import i18next from 'i18next';
import { toast } from 'sonner';

const SESSION_ID = /^[a-f0-9]{64}$/i;

function message(key) {
    return i18next.t(`reportSurface:reportArtifacts.${key}`);
}
function listErrorMessage() {
    return i18next.t('reportArchive:messages.fetchError', {
        defaultValue: 'Could not load the report archive',
    });
}

export class ReportArtifactIntegration {
    constructor(service) {
        this.service = service;
        this.inFlight = new Map();
    }

    canArchive(session) {
        return Boolean(
            session?.archiveGrant?.token
            && session.archiveGrant.contractVersion === 2
            && session.archiveGrant.format === 'repx'
            && this.idempotencyKey(session),
        );
    }

    idempotencyKey(session) {
        const sessionId = String(session?.sessionId || '').trim();
        return SESSION_ID.test(sessionId) ? `report-session-${sessionId.toLowerCase()}` : null;
    }

    grantRequired() {
        return {
            success: false,
            code: 'REPORT_ARTIFACT_GRANT_REQUIRED',
            error: message('grantRequired'),
        };
    }

    async archive(session, { idempotencyKey, description } = {}) {
        if (!this.canArchive(session)) return this.grantRequired();
        if (!idempotencyKey || idempotencyKey !== this.idempotencyKey(session)) {
            return {
                success: false,
                code: 'REPORT_ARTIFACT_IDEMPOTENCY_REQUIRED',
                error: message('idempotencyRequired'),
            };
        }
        const existing = this.inFlight.get(idempotencyKey);
        if (existing) return existing;
        const operation = (async () => {
            try {
                const response = await this.service.create(
                    session.archiveGrant.token,
                    idempotencyKey,
                    { description },
                );
                toast.success(message('saved'));
                return { success: true, data: response.data };
            } catch (error) {
                const errorMessage = message('requestFailed');
                toast.error(errorMessage);
                return { success: false, error: errorMessage, code: error?.code };
            } finally {
                this.inFlight.delete(idempotencyKey);
            }
        })();
        this.inFlight.set(idempotencyKey, operation);
        return operation;
    }

    async list() {
        try {
            return await this.service.list();
        } catch (error) {
            const errorMessage = listErrorMessage();
            toast.error(errorMessage);
            return { success: false, error: errorMessage, code: error?.code };
        }
    }

    async view(reportId) {
        try {
            return await this.service.getViewerAccess(reportId);
        } catch (error) {
            const errorMessage = message('requestFailed');
            toast.error(errorMessage);
            return { success: false, error: errorMessage, code: error?.code };
        }
    }

    async download(reportId, format = 'repx') {
        try {
            const response = await this.service.download(reportId, format);
            return { success: true, data: response?.data ?? response };
        } catch (error) {
            const errorMessage = message('requestFailed');
            toast.error(errorMessage);
            return { success: false, error: errorMessage, code: error?.code };
        }
    }
}
